import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import isEmail from 'validator/lib/isEmail';
import debounce from 'lodash/debounce';
import styled from '@emotion/styled';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import Button, { LoadingButton } from '@atlaskit/button';
import { Box, Text } from '@atlaskit/primitives';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import { token } from '@atlaskit/tokens';
import { N500 } from '@atlaskit/theme/colors';
import { ProgressIndicator } from '@atlaskit/progress-indicator';
import Form, { ValidMessage } from '@atlaskit/form';
import SectionMessage from '@atlaskit/section-message';
import { NotificationTypes, successNotificationBuilder, useNotification, InfoPanel } from '@css-xp/base';
import { STATSIG_FEATURE_GATES, SUBSCRIPTION_CONSTANTS } from '@css-xp/ppc-constants';
import { getSupportUrl } from '@css-xp/ppc-common';
import { SearchField } from '../../../billing-profile/src/components/search-and-filter/search-field';
import {
  useOfferingsAndPricingPlansStore,
  useStore,
  useTransferCloudSiteOrOrgNameMap,
  useOrgProductInstancesMap,
} from '../../../../store';
import { fetchEntitlementsForEntityWithContactEmails, processEntity, validateEntity } from '../utils/api';
import { AddCustomerEmailField } from './add-customer-email-field';
import { getDataForStore } from '../../../billing-profile/src/store-utils';
import { OperationType } from '../../../../types';
import {
  EntitlementListWithEmailFetchStatus,
  ExternalProductsList,
  SelectType,
} from '../../../billing-profile/src/types';
import { fetchCloudSiteProductsBelongingToOtherTxa } from '../../../billing-profile/src/api';
import { CloudSiteContainer } from '../../../billing-profile/src/components/cloud-sites-container';
import { ModalFooterWrapper, ModalHeaderWrapper, ModalTitleWrapper } from './transfer-entitlements-modal.styles';
import { getValidEntry } from '../utils/transfer-entitlements-utils';

interface TransferEntitlementsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface User {
  email: string;
  error?: string | undefined;
}

enum SCREEN_NAME {
  TRANSFER_FORM,
  SEARCH_RESULTS,
}

const StyledCustomerContactInputField = styled.div`
  .cart-add-customer-email-container {
    .cart-transfer-email-list {
      .cart-email-item {
        > div {
          margin-top: ${token('space.250')};
        }
      }
    }
  }
`;

const someEntitlementIsUnprovisionedErrorMsg =
  'Some of the subscriptions linked to the search criteria are not provisioned and are excluded from the search result';

const allEntitlementsAreUnprovisionedErrorMsg =
  'Subscriptions linked to the search criteria are not provisioned and are excluded from the search result';

const getEntityValidSuccessMessage = (entity: string) => {
  const processedEntity = processEntity(entity);

  if (processedEntity.entitlementNumber) {
    return 'Entitlement identified';
  }

  return 'Site identified';
};

export const TransferEntitlementsModal: React.FC<TransferEntitlementsModalProps> = ({ isOpen, onClose }) => {
  const { txaId } = useParams<{ txaId: string }>();
  const [
    { products, linkedEntitlements, sidebarFilter, featureFlags, invoiceGroupId, billingProfiles },
    actions,
  ] = useStore();
  const { searchQuery } = sidebarFilter ?? {};
  const offeringsAndPricingPlans = useOfferingsAndPricingPlansStore();
  const [cloudSiteOrOrgNameMapForTransfer] = useTransferCloudSiteOrOrgNameMap();
  const orgProductInstancesMap = useOrgProductInstancesMap();

  const [emailList, setEmailList] = useState<Map<string, User>>(new Map());
  const [selectedProgressIndex, setSelectedProgressIndex] = useState(0);
  const [transferExternalProductsList, setTransferExternalProductsList] = useState<ExternalProductsList>([]);
  const [hasNoTransferSearchResults, setHasNoTransferSearchResults] = useState(false);
  const [showTransferSearchResults, setShowTransferSearchResults] = useState(false);
  const [isSubmittingTransferSearchForm, setIsSubmittingTransferSearchForm] = useState(false);
  const [
    entitlementListWithEmailFetchStatus,
    setEntitlementListWithEmailFetchStatus,
  ] = useState<EntitlementListWithEmailFetchStatus>(EntitlementListWithEmailFetchStatus.IDLE);
  const [checkedItemMap, setCheckedItemMap] = React.useState<Record<string, SelectType>>({});
  const [isValidatingEntity, setIsValidatingEntity] = useState(false);
  const [isEntityValid, setIsEntityValid] = useState(false);
  const [entityValidationError, setEntityValidationError] = useState<string | undefined>();
  const [transferErrorMessage, setTransferErrorMessage] = useState<string | undefined>();

  const isEntitlementListWithEmailLoading =
    entitlementListWithEmailFetchStatus === EntitlementListWithEmailFetchStatus.PENDING;

  const [screenValues] = useState([SCREEN_NAME.TRANSFER_FORM, SCREEN_NAME.SEARCH_RESULTS]);
  const { notificationSent, sendSuccessNotification, setNotificationSent } = useNotification();

  const showInactiveProducts = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_INACTIVE_PRODUCTS_IN_UOF] || false;
  const shouldShowAddons = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_ADD_ONS] || false;
  const showSandboxProducts = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_SANDBOX_SUPPORT_FOR_SUBSCRIPTIONS] || false;
  const isSandboxDecouplingEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_SANDBOX_DECOUPLING] || false;
  const isJPDAsJiraFamilyEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_JPD_AS_JIRA_FAMILY] || false;
  const isNonCommercialPricingEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_NON_COMMERCIAL_PRICING] || false;
  const isMultiCurrencyEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_MULTI_CURRENCY_CHANGES] || false;
  const isFutureDatedEntitlementEnabled =
    featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_SUPPORT_FOR_FUTURE_DATED_ENTITLEMENTS] || false;

  const isTWCPhase1Enabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_TWC_SUPPORT_PHASE_1] || false;
  const isAddOnsPhase2Enabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_ADD_ONS_PHASE_V2] || false;
  const isTWCPhase2Enabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_TWC_SUPPORT_PHASE_2] || false;
  const isMultiInstanceAppsEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_MULTI_INSTANCE_APPS] || false;

  React.useEffect(() => {
    const validate = debounce(async () => {
      setIsValidatingEntity(true);
      setIsEntityValid(false);
      setTransferErrorMessage(undefined);

      try {
        const response = await validateEntity({
          entity: searchQuery,
          transactionAccountId: txaId,
        });

        if (response.valid) {
          setIsEntityValid(true);
        } else {
          setIsEntityValid(false);
          setEntityValidationError(response.reason);
        }
      } catch (error: any) {
        setEntityValidationError(error?.message ?? 'Something went wrong.');
      }

      setEmailList(new Map());
      setIsValidatingEntity(false);
    }, 500);

    if (!searchQuery) {
      setIsEntityValid(false);
      setTransferErrorMessage(undefined);
      return;
    }

    const processedEntity = processEntity(searchQuery);
    if (!processedEntity.isValid) {
      setEntityValidationError('Invalid entitlement ID or site URL');
      setIsEntityValid(false);
    } else {
      setEntityValidationError(undefined);
      validate();
    }
  }, [searchQuery, txaId]);

  const handleCheckedItems = (type: SelectType, identifier: string) => {
    if (!type || !identifier) return;

    if (type === SelectType.ENTITLEMENT) {
      setCheckedItemMap((prevCheckedItemMap: Record<string, SelectType>) => {
        const newCheckedItemMap = { ...prevCheckedItemMap };
        if (newCheckedItemMap[identifier]) {
          delete newCheckedItemMap[identifier];
        } else {
          newCheckedItemMap[identifier] = type;
        }
        return newCheckedItemMap;
      });
    }
  };

  const handleTransferSubmit = async (data: { [key: string]: string }) => {
    const { ...emailObj } = data;
    const processedEntity = processEntity(searchQuery);
    const payload = {
      entity: (processedEntity.entitlementNumber || processedEntity.siteURL) as string,
      emails: Object.values(emailObj).map((email) => email.trim()),
      transactionAccountId: txaId,
    };

    /**
     * Remove the searched entitlement or cloudsite from the store, if it is already present.
     */
    actions.removeProductsAndApps({ ...processedEntity, removeOnlyTransferEntitlements: true });

    try {
      setEntitlementListWithEmailFetchStatus(EntitlementListWithEmailFetchStatus.PENDING);
      const productSubscriptions = await fetchEntitlementsForEntityWithContactEmails(payload);
      const selectedBillingProfile = billingProfiles?.find((profile) => profile.id === invoiceGroupId);
      const productsAndAppData = getDataForStore({
        entitlementSubscriptionsWrapper: productSubscriptions,
        offeringsAndpricingPlans: offeringsAndPricingPlans,
        operationType: OperationType.TRANSFER,
        hasAllData: true,
        includeOrgProducts: true,
        showSandboxProducts,
        isSandboxDecouplingEnabled,
        isFutureDatedEntitlementEnabled,
        isMultiCurrencyEnabled,
        destinationCurrency: selectedBillingProfile?.currency || '',
        isNonCommercialPricingEnabled,
        destinationPricingEligibility: selectedBillingProfile?.shipToPriceEligibility || '',
        isTWCPhase1Enabled,
        isTWCPhase2Enabled,
        isAddOnsPhase2Enabled,
        isMultiInstanceAppsEnabled,
      });
      if (productsAndAppData) {
        actions.addProductsAndApps(productsAndAppData);
      }

      const hasUnprovisionedSubscription = !!productSubscriptions?.nodes?.find?.(
        (subscription) => !(subscription?.cloudSiteHostname || subscription?.orgId)
      );

      if (hasUnprovisionedSubscription) {
        const isEmptyResponse = isEmpty(productsAndAppData?.products);

        setTransferErrorMessage(
          isEmptyResponse ? allEntitlementsAreUnprovisionedErrorMsg : someEntitlementIsUnprovisionedErrorMsg
        );

        // It is possible the complete response is empty. This happens when we search for an unprovisioned entitlement.
        // In this case, we should allow the user to search again.
        if (isEmptyResponse) {
          setIsSubmittingTransferSearchForm(false);
          return;
        }
      }

      if (productsAndAppData) {
        const allProducts = Object.values(productsAndAppData.products);
        const cloudSiteIds = allProducts.map(({ cloudSiteId }) => cloudSiteId);
        if (cloudSiteIds && cloudSiteIds.length) {
          const externalProductListResponse = await fetchCloudSiteProductsBelongingToOtherTxa({
            currentTransactionAccountId: txaId,
            cloudSiteIds,
          });
          setTransferExternalProductsList(externalProductListResponse);
        }
      }
      setIsSubmittingTransferSearchForm(false);
      setShowTransferSearchResults(true);
      setEntitlementListWithEmailFetchStatus(EntitlementListWithEmailFetchStatus.SUCCESS);
      setSelectedProgressIndex(SCREEN_NAME.SEARCH_RESULTS);

      if (!notificationSent && !hasNoTransferSearchResults) {
        const notificationOptions = successNotificationBuilder()
          .withId('externally-managed-subscriptions-added')
          .withHeader('Externally-managed subscriptions added')
          .withMessage("They'll be transferred to your billing account when you confirm your order.")
          .withDismissAfter(5000)
          .isVersion(NotificationTypes.FLAG)
          .build();
        sendSuccessNotification(notificationOptions);
        setNotificationSent(true);
      }
      if (hasNoTransferSearchResults) {
        setHasNoTransferSearchResults(false);
      }
    } catch (e: any) {
      setEntitlementListWithEmailFetchStatus(EntitlementListWithEmailFetchStatus.ERROR);
      setIsSubmittingTransferSearchForm(false);
      setTransferErrorMessage(e.message);
      setHasNoTransferSearchResults(true);
    }
  };

  const handlePrev = () => {
    if (selectedProgressIndex === SCREEN_NAME.SEARCH_RESULTS) {
      setSelectedProgressIndex(SCREEN_NAME.TRANSFER_FORM);
    }
  };

  const handleSubmit = async () => {
    setTransferErrorMessage(undefined);

    if (selectedProgressIndex === SCREEN_NAME.TRANSFER_FORM) {
      const emailObj: Record<string, string> = {};
      emailList.forEach(({ email }, key) => {
        emailObj[key] = email;
      });
      if (searchQuery && Object.values(emailObj).length > 0) {
        setIsSubmittingTransferSearchForm(true);
        await handleTransferSubmit(emailObj);
      }
    }

    if (selectedProgressIndex === SCREEN_NAME.SEARCH_RESULTS) {
      /**
       * If checkedItemMap is org product, find the corresponding instances and add them to the checkedItemMap.
       */
      Object.entries(checkedItemMap).forEach(([identifier, type]) => {
        actions.toggleSelectedItem(identifier, type, true, true);
        if (isTWCPhase2Enabled) {
          products[identifier]?.isOrgProduct &&
            orgProductInstancesMap[identifier]?.forEach((instance) => {
              actions.toggleSelectedItem(instance.id, type, true, true);
            });
        }
      });
      onClose();
      actions.setSearchQuery('');
      setCheckedItemMap({});
      setEmailList(new Map());
      setSelectedProgressIndex(SCREEN_NAME.TRANSFER_FORM);
    }
  };

  const handleOnClose = () => {
    onClose();
    actions.setSearchQuery('');
    setCheckedItemMap({});
    setEmailList(new Map());
    setSelectedProgressIndex(SCREEN_NAME.TRANSFER_FORM);
  };

  const trimSearchQuery = (searchQuery: string) => {
    return processEntity(searchQuery, true).isValid ? searchQuery.trim() : searchQuery;
  };

  const isTransferSearchSubmitBtnDisabled = useMemo(() => {
    const emailListArray = Array.from(emailList);
    if (!emailListArray.length || !processEntity(searchQuery).isValid || !isEntityValid) {
      return true;
    }

    const invalidEmail = emailListArray.find(([_key, { email }]) => {
      return !email || !isEmail(email);
    });

    return !!invalidEmail;
  }, [emailList, searchQuery, isEntityValid]);

  const clearSearchQuery = () => {
    actions.setSearchQuery('');
    setEmailList(new Map());
  };

  const entityValidationErrorMessage = useMemo(() => {
    if (entityValidationError) {
      if (entityValidationError === SUBSCRIPTION_CONSTANTS.ENTITY_INVALID_REASON.INACTIVE_BILLING_STATUS) {
        return (
          <>
            Only subscriptions from active sites and entitlement ID can be transferred, for help, contact{' '}
            {getSupportUrl()}.
          </>
        );
      }

      if (entityValidationError === SUBSCRIPTION_CONSTANTS.ENTITY_INVALID_REASON.ANNUAL_ENTITY) {
        return (
          <>Only monthly orders can be placed. To create a quote for annual subscriptions, contact {getSupportUrl()}.</>
        );
      }

      if (entityValidationError === SUBSCRIPTION_CONSTANTS.ENTITY_INVALID_REASON.HAMS_ENTITY) {
        return (
          <>
            This entitlement/site isn't supported by Atlassian's new billing engine. For help, contact {getSupportUrl()}
            .
          </>
        );
      }

      if (entityValidationError === SUBSCRIPTION_CONSTANTS.ENTITY_INVALID_REASON.SAME_TXA) {
        return (
          <>
            This entitlement/site belongs to another customer. Only subscriptions for this customer can be transferred
            from another billing account. For help, contact {getSupportUrl()}.
          </>
        );
      }

      if (entityValidationError === SUBSCRIPTION_CONSTANTS.ENTITY_INVALID_REASON.APP_ENTITY) {
        return (
          <>
            This searched entitlement is an app, use a core product’s entitlement to proceed, or contact{' '}
            {getSupportUrl()}.
          </>
        );
      }

      if (entityValidationError === SUBSCRIPTION_CONSTANTS.ENTITY_INVALID_REASON.ADD_ON_ENTITY) {
        return (
          <>
            This searched entitlement is an add-on, use a core product’s entitlement to proceed, or contact{' '}
            {getSupportUrl()}.
          </>
        );
      }

      return entityValidationError;
    }

    return '';
  }, [entityValidationError]);

  return (
    <ModalTransition>
      {isOpen && (
        <Modal testId="transfer-entitlements-modal" shouldScrollInViewport height={690} width={752}>
          <ModalHeader>
            <ModalHeaderWrapper>
              <ModalTitleWrapper className="flex align-center justify-space-between">
                <ModalTitle>Select subscriptions you want to transfer</ModalTitle>
                <Button appearance="subtle" onClick={handleOnClose} testId="transfer-entitlements-close-modal-button">
                  <CrossIcon size="small" label="Close Modal" primaryColor={token('color.text.subtle', N500)} />
                </Button>
              </ModalTitleWrapper>
              <Text size="small" color="color.text.subtlest">
                Move externally-managed subscriptions or entire sites to your billing account.
              </Text>
            </ModalHeaderWrapper>
          </ModalHeader>
          <ModalBody>
            {transferErrorMessage && (
              <Box paddingBlockEnd="space.150" testId="transfer-error-message">
                <InfoPanel type="error" message={<p>{transferErrorMessage}</p>} />
              </Box>
            )}
            {selectedProgressIndex === SCREEN_NAME.TRANSFER_FORM && (
              <>
                <Form onSubmit={handleSubmit}>
                  {({ formProps }) => (
                    <form
                      {...formProps}
                      onKeyPress={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                        }
                      }}
                    >
                      <SearchField
                        searchQuery={searchQuery}
                        onQueryChange={(searchQuery: string) =>
                          actions.setSidebarFilter({
                            searchQuery: trimSearchQuery(searchQuery),
                          })
                        }
                        clearSearchQuery={clearSearchQuery}
                        placeholder={'Enter site URL or entitlement ID'}
                        showSearchIconBeforeInput={true}
                        isLoading={isValidatingEntity}
                      />
                      {searchQuery && !entityValidationError && !isValidatingEntity && isEntityValid && (
                        <ValidMessage>{getEntityValidSuccessMessage(searchQuery)}</ValidMessage>
                      )}

                      {searchQuery && processEntity(searchQuery, true).isValid && isEntityValid && (
                        <StyledCustomerContactInputField>
                          <AddCustomerEmailField
                            emailList={emailList}
                            setEmailList={setEmailList}
                            hideHelperText={true}
                            showAddContactAsButton={true}
                            showRemoveAsIcon={true}
                            showTextFieldLabel={true}
                          />
                        </StyledCustomerContactInputField>
                      )}
                    </form>
                  )}
                </Form>
                {searchQuery && entityValidationErrorMessage && (
                  <Box paddingBlockStart="space.150">
                    <SectionMessage appearance="error">{entityValidationErrorMessage}</SectionMessage>
                  </Box>
                )}
              </>
            )}
            {selectedProgressIndex === SCREEN_NAME.SEARCH_RESULTS && showTransferSearchResults && (
              <CloudSiteContainer
                cloudSiteOrOrgNameMap={getValidEntry({
                  cloudSiteOrOrgNameMapForTransfer,
                  searchQuery,
                  products,
                  orgProductInstancesMap,
                  isTWCPhase2Enabled,
                  linkedEntitlements,
                  isAddOnsPhase2Enabled,
                })}
                onChange={handleCheckedItems}
                isEntitlementListWithEmailLoading={isEntitlementListWithEmailLoading}
                externalProductsList={transferExternalProductsList}
                isRenderingTransferFlowEntitlements
                showInactiveProducts={showInactiveProducts}
                shouldShowAddons={shouldShowAddons}
                shouldShowSandboxStatus={showSandboxProducts || isSandboxDecouplingEnabled}
                isJPDAsJiraFamilyEnabled={isJPDAsJiraFamilyEnabled}
                isCheckboxSelectionEnabled={true}
                checkedItemMap={checkedItemMap}
                setCheckedItemMap={setCheckedItemMap}
                hideFoundLabel={true}
                hideExternalListButton={true}
                isDefaultExpanded={true}
                isTWCPhase2Enabled={isTWCPhase2Enabled}
              />
            )}
          </ModalBody>
          <ModalFooter>
            <ModalFooterWrapper className="flex align-center justify-space-between">
              <Button
                isDisabled={selectedProgressIndex === 0}
                onClick={handlePrev}
                testId="transfer-entitlements-prev-button"
              >
                Prev
              </Button>

              <ProgressIndicator
                testId="transfer-entitlements-progress-indicator"
                values={screenValues}
                selectedIndex={selectedProgressIndex}
                size="default"
              />

              <LoadingButton
                type="submit"
                onClick={handleSubmit}
                isLoading={isSubmittingTransferSearchForm}
                isDisabled={
                  isTransferSearchSubmitBtnDisabled ||
                  (selectedProgressIndex === SCREEN_NAME.SEARCH_RESULTS && isEmpty(checkedItemMap))
                }
                testId="transfer-entitlements-next-button"
                appearance={selectedProgressIndex === SCREEN_NAME.TRANSFER_FORM ? 'default' : 'primary'}
              >
                {selectedProgressIndex === SCREEN_NAME.TRANSFER_FORM ? 'Next' : 'Add to order'}
              </LoadingButton>
            </ModalFooterWrapper>
          </ModalFooter>
        </Modal>
      )}
    </ModalTransition>
  );
};
